import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { getErrorMessage } from '../../../lib/errors';
import { useAuthStore } from '../../../stores/authStore';
import { CardNB, ButtonNB, BadgeNB, IconNB } from '../../../components/ui-nb';
import EmployeeInviteModal from '../../../components/EmployeeInviteModal';
import type { Tables } from '@tellowup/db';
import { error as logError } from '../../../lib/logger';

interface Branch {
  id: string;
  name: string;
  address: string;
  phone: string;
  schedule_start: string;
  schedule_end: string;
  organization_id: string;
}

interface OrderItem {
  id: string;
  customer_name: string;
  device: string;
  status: string;
  created_at: string;
}

// Unified list item — active employee or pending invite
interface EmployeeRow {
  id: string;
  name: string;
  role: string;
  status: 'active' | 'pending';
}

// Intersection types for joined Supabase results
type BranchUserRow = Tables<'branch_user'> & {
  profiles?: Tables<'profiles'> | null;
};

type InvitationRow = Tables<'invitation'>;

export default function BranchDetail() {
  const { id, lang } = useParams();
  const navigate = useNavigate();
  const organizationId = useAuthStore(s => s.user?.organizationId);
  const [branch, setBranch] = useState<Branch | null>(null);
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [branchCount, setBranchCount] = useState(0);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const locale = lang || 'en';

  useEffect(() => {
    async function fetchData() {
      if (!id) return;
      try {
        const [branchRes, usersRes, invitesRes, ordersRes, countRes] = await Promise.all([
          supabase.from('branches').select('*, status_config(*)').eq('id', id),
          supabase.from('branch_user').select('*, profiles(id, full_name, role)').eq('branch_id', id),
          supabase.from('invitation').select('*').eq('branch_id', id).is('accepted_at', null),
          supabase.from('orders').select('id, customer_name, device, status, created_at').eq('branch_id', id).order('created_at', { ascending: false }).limit(5),
          supabase.from('branches').select('*', { count: 'exact', head: true }).eq('organization_id', organizationId || ''),
        ]);
        if (branchRes.error) throw branchRes.error;

        setBranch(branchRes.data?.[0] as Branch | null);

        // Map active employees (from branch_user join)
        const active: EmployeeRow[] = (usersRes.data as BranchUserRow[])?.map((bu: BranchUserRow) => ({
          id: bu.profiles?.id || '',
          name: bu.profiles?.full_name || '—',
          role: bu.profiles?.role || 'technician',
          status: 'active' as const,
        })) || [];

        // Map pending invitations
        const pending: EmployeeRow[] = (invitesRes.data as InvitationRow[])?.map((inv: InvitationRow) => ({
          id: inv.id,
          name: inv.email,
          role: inv.role || 'technician',
          status: 'pending' as const,
        })) || [];

        setEmployees([...active, ...pending]);
        setOrders((ordersRes.data as OrderItem[]) || []);
        setBranchCount(countRes.count ?? 0);
      } catch (err) {
        logError('Error fetching data', { error: getErrorMessage(err) });
        setError(getErrorMessage(err) || 'Failed to load branch');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id, organizationId]);

  const handleDelete = async () => {
    if (!branch || !organizationId) return;
    if (!window.confirm(`Are you sure you want to delete "${branch.name}"? This action cannot be undone.`)) return;

    setDeleting(true);
    try {
      const { count, error: countErr } = await supabase
        .from('branches')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', organizationId);
      if (countErr) throw countErr;
      if (count !== null && count <= 1) {
        alert('Cannot delete the last branch. Every organization needs at least one branch.');
        setDeleting(false);
        return;
      }

      await supabase
        .from('profiles')
        .update({ branch_id: null })
        .eq('branch_id', branch.id);

      const { error: delErr } = await supabase
        .from('branches')
        .delete()
        .eq('id', branch.id);
      if (delErr) throw delErr;

      navigate(`/${locale}/admin/branches`);
    } catch (err) {
      logError('Error deleting branch', { error: getErrorMessage(err) });
      alert('Failed to delete branch. Make sure there are no orders associated with it.');
    } finally {
      setDeleting(false);
    }
  };

  const handleRemoveEmployee = async (row: EmployeeRow) => {
    if (!branch) return;
    const message = row.status === 'active'
      ? 'Remove this employee from this branch?'
      : 'Revoke this invitation?';
    if (!window.confirm(message)) return;

    setRemovingId(row.id);
    try {
      if (row.status === 'active') {
        const { error: removeErr } = await supabase
          .from('branch_user')
          .delete()
          .eq('branch_id', branch.id)
          .eq('user_id', row.id);
        if (removeErr) throw removeErr;
      } else {
        const { error: revokeErr } = await supabase
          .from('invitation')
          .delete()
          .eq('id', row.id);
        if (revokeErr) throw revokeErr;
      }

      setEmployees((prev) => prev.filter((e) => e.id !== row.id));
    } catch (err) {
      logError('Error removing', { error: getErrorMessage(err) });
      alert(row.status === 'active' ? 'Failed to remove employee' : 'Failed to revoke invitation');
    } finally {
      setRemovingId(null);
    }
  };

  const handleInviteSuccess = () => {
    setShowEmployeeModal(false);
    if (!id) return;
    async function refresh() {
      const [usersRes, invitesRes] = await Promise.all([
        supabase.from('branch_user').select('*, profiles(id, full_name, role)').eq('branch_id', id),
        supabase.from('invitation').select('*').eq('branch_id', id).is('accepted_at', null),
      ]);

      const active: EmployeeRow[] = (usersRes.data as BranchUserRow[])?.map((bu: BranchUserRow) => ({
        id: bu.profiles?.id || '',
        name: bu.profiles?.full_name || '—',
        role: bu.profiles?.role || 'technician',
        status: 'active' as const,
      })) || [];

      const pending: EmployeeRow[] = (invitesRes.data as InvitationRow[])?.map((inv: InvitationRow) => ({
        id: inv.id,
        name: inv.email,
        role: inv.role || 'technician',
        status: 'pending' as const,
      })) || [];

      setEmployees([...active, ...pending]);
    }
    refresh();
  };

  if (loading) return <p className="text-nb-text-muted">Loading...</p>;
  if (error || !branch) return <p className="text-nb-red">{error || 'Branch not found'}</p>;

  const activeEmployees = employees.filter((e) => e.status === 'active');
  const pendingEmployees = employees.filter((e) => e.status === 'pending');

  return (
    <>
      <div className="mb-6">
        <Link
          to={`/${locale}/admin/branches`}
          className="text-nb-black text-sm inline-flex items-center gap-1"
        >
          <IconNB name="chevronLeft" size={16} />
          Back to branches
        </Link>
      </div>

      {/* Branch Info Card */}
      <CardNB className="p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <h1 className="text-xl font-bold text-nb-black">
            {branch.name}
          </h1>
          <div className="flex gap-2">
            <Link to={`/${locale}/admin/branches/${branch.id}/edit`}>
              <ButtonNB variant="secondary" size="sm">
                Edit
              </ButtonNB>
            </Link>
            {branchCount > 1 && (
              <ButtonNB variant="secondary" size="sm" onClick={handleDelete} loading={deleting}
                className="text-nb-red border-nb-red">
                Delete
              </ButtonNB>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-nb-text-muted">Address</p>
            <p className="text-nb-black">{branch.address}</p>
          </div>
          <div>
            <p className="text-nb-text-muted">Phone</p>
            <p className="text-nb-black">{branch.phone || '—'}</p>
          </div>
          <div>
            <p className="text-nb-text-muted">Schedule</p>
            <p className="text-nb-black">{branch.schedule_start ? `${branch.schedule_start} - ${branch.schedule_end}` : '—'}</p>
          </div>
        </div>
      </CardNB>

      {/* Employees Card */}
      <CardNB className="p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-nb font-bold text-nb-black">Employees</h2>
          <ButtonNB variant="primary" size="sm" onClick={() => setShowEmployeeModal(true)}>
            <IconNB name="plus" size={16} />
            Add Employee
          </ButtonNB>
        </div>

        {employees.length === 0 ? (
          <p className="text-nb-text-muted text-sm py-2">No employees or pending invitations</p>
        ) : (
          <div className="flex flex-col gap-1">
            {activeEmployees.map((employee) => (
              <div
                key={employee.id}
                className="flex items-center justify-between py-2 border-b border-nb-black/10"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <p className="font-nb font-medium text-nb-black">{employee.name}</p>
                  <BadgeNB variant="success">Active</BadgeNB>
                  <span className="text-nb-text-muted text-sm capitalize">
                    {employee.role}
                  </span>
                </div>
                <button
                  onClick={() => handleRemoveEmployee(employee)}
                  disabled={removingId === employee.id}
                  title="Remove employee"
                  className={`text-lg opacity-${removingId === employee.id ? 50 : 100} cursor-pointer`}
                >
                  <IconNB name="close" size={16} className="text-nb-red" />
                </button>
              </div>
            ))}

            {pendingEmployees.map((inv) => (
              <div
                key={inv.id}
                className="flex items-center justify-between py-2 border-b border-nb-black/10"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <p className="font-nb font-medium text-nb-black">{inv.name}</p>
                  <BadgeNB variant="warning">Pending</BadgeNB>
                  <span className="text-nb-text-muted text-sm capitalize">
                    {inv.role}
                  </span>
                </div>
                <button
                  onClick={() => handleRemoveEmployee(inv)}
                  disabled={removingId === inv.id}
                  title="Revoke invitation"
                  className={`text-lg opacity-${removingId === inv.id ? 50 : 100} cursor-pointer`}
                >
                  <IconNB name="close" size={16} className="text-nb-red" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardNB>

      {/* Recent Orders Card */}
      <CardNB className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-nb font-bold text-nb-black">Recent orders</h2>
          <button className="text-nb-black text-sm bg-none border-none cursor-pointer">
            View all orders
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-nb-black">
                <th className="pb-2 font-nb font-medium text-nb-text-muted">Order</th>
                <th className="pb-2 font-nb font-medium text-nb-text-muted">Client</th>
                <th className="pb-2 font-nb font-medium text-nb-text-muted">Device</th>
                <th className="pb-2 font-nb font-medium text-nb-text-muted">Status</th>
                <th className="pb-2 font-nb font-medium text-nb-text-muted">Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id.slice(0, 8)} className="border-b border-nb-black/10">
                  <td className="py-2 text-nb-black">{order.id.slice(0, 8)}</td>
                  <td className="py-2 text-nb-text-secondary">{order.customer_name}</td>
                  <td className="py-2 text-nb-text-secondary">{order.device}</td>
                  <td className="py-2">
                    <BadgeNB variant={order.status === 'Completed' ? 'success' : order.status === 'In Progress' ? 'info' : 'warning'}>
                      {order.status}
                    </BadgeNB>
                  </td>
                  <td className="py-2 text-nb-text-muted">{new Date(order.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardNB>

      {showEmployeeModal && branch && (
        <EmployeeInviteModal
          branchId={branch.id}
          branchName={branch.name}
          organizationId={organizationId || ''}
          locale={locale}
          onClose={() => setShowEmployeeModal(false)}
          onSuccess={handleInviteSuccess}
        />
      )}
    </>
  );
}